import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { useEffect } from "react";
import { useRole } from "hooks/common";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { exerciseSelector, setExerciseState } from "store/features/exercises";

type SubscriptionParams = {
  studentId: number;
  courseId: number;
  lessonId: number;
  chapterId: number;
};

export const useReactQuerySubscription = ({
  studentId,
  courseId,
  lessonId,
  chapterId,
}: SubscriptionParams) => {
  const { isUserTeacher } = useRole();
  const dispatch = useAppDispatch();
  const { exercises: localExercises } = useAppSelector(exerciseSelector);

  useEffect(() => {
    if (!isUserTeacher) return;

    // Use the same base URL as the API, but point to the WebSocket STOMP endpoint
    const baseUrl =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";
    const sock = new SockJS(`${baseUrl}/notbotan`);

    const client = new Client({
      // wrap SockJS for STOMP
      webSocketFactory: () => sock,
      // auto-reconnect, logging, etc.
      reconnectDelay: 5000,
      onConnect: () => {
        // 2) only after connect, subscribe to your topic:
        client.subscribe(
          `/topic/student-progress/${studentId}` +
            `/courses/${courseId}` +
            `/lessons/${lessonId}` +
            `/chapters/${chapterId}/exercises`,
          (message) => {
            const data = JSON.parse(message.body);
            console.log("WebSocket exercise update received:", data);
            const updatedExercises = localExercises.map((exercise) => {
              if (exercise.id === data.id) {
                console.log(
                  "Updating exercise:",
                  exercise.id,
                  "with new data:",
                  data
                );
                return data;
              }
              return exercise;
            });
            console.log(
              "Dispatching updated exercises to Redux:",
              updatedExercises
            );
            dispatch(setExerciseState(updatedExercises));
          }
        );
      },
      onStompError: (frame) => {
        console.error("Broker error:", frame.headers, frame.body);
      },
    });

    client.activate();

    return () => {
      client.deactivate();
    };
  }, [
    studentId,
    courseId,
    lessonId,
    chapterId,
    isUserTeacher,
    localExercises,
    dispatch,
  ]);
};
